"use client";

/**
 * Sidebar izquierdo de Rubripedia: ÁRBOL completo de categorías (todos los
 * niveles) rankeado por uso con conteo de subárbol, expandible; y etiquetas
 * populares. Links con URLs amigables (SEO). Resalta el nodo activo por la URL.
 */

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { Layers, Tag as TagIcon, Sparkles, ChevronRight, ChevronDown } from "lucide-react";
import { categoryPath, tagPath, slugify } from "@/lib/utils/slug";

interface Cat { id: number; name: string; parent_id: number | null; count: number }
interface TagItem { name: string; count: number }
interface TreeNode extends Cat { children: TreeNode[] }

function buildTree(flat: Cat[]): TreeNode[] {
  const byId = new Map<number, TreeNode>();
  flat.forEach((c) => byId.set(c.id, { ...c, children: [] }));
  const roots: TreeNode[] = [];
  byId.forEach((node) => {
    const parent = node.parent_id != null ? byId.get(node.parent_id) : null;
    if (parent) parent.children.push(node);
    else roots.push(node);
  });
  const sortRec = (nodes: TreeNode[]) => {
    nodes.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
    nodes.forEach((n) => sortRec(n.children));
  };
  sortRec(roots);
  return roots;
}

function CatBranch({ node, activeSlug, depth }: { node: TreeNode; activeSlug: string | null; depth: number }) {
  const path = categoryPath(node.name, node.id);
  const active = activeSlug === path.split("/").pop();
  const hasKids = node.children.length > 0;
  const [open, setOpen] = useState(depth === 0 && active);

  // Abre la rama si algún descendiente está activo
  const containsActive = useMemo(() => {
    const walk = (n: TreeNode): boolean =>
      activeSlug === categoryPath(n.name, n.id).split("/").pop() || n.children.some(walk);
    return walk(node);
  }, [node, activeSlug]);
  useEffect(() => { if (containsActive) setOpen(true); }, [containsActive]);

  return (
    <div>
      <div className={`flex items-center gap-0.5 rounded-lg pr-1 transition-colors ${active ? "bg-primary-bg" : "hover:bg-muted"}`}
        style={{ paddingLeft: `${depth * 10}px` }}>
        {hasKids ? (
          <button type="button" onClick={() => setOpen((v) => !v)} aria-label={open ? "Colapsar" : "Expandir"}
            className="shrink-0 p-1 text-muted-foreground hover:text-foreground">
            {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          </button>
        ) : <span className="w-5 shrink-0" />}
        <Link href={path}
          className={`flex flex-1 items-center justify-between gap-2 py-1.5 pr-1 text-[13px] transition-colors ${
            active ? "font-medium text-primary" : "text-foreground/80"
          }`}>
          <span className="truncate">{node.name}</span>
          <span className={`shrink-0 rounded px-1.5 text-[10px] tabular-nums ${active ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
            {node.count}
          </span>
        </Link>
      </div>
      {hasKids && open && (
        <div>{node.children.map((c) => <CatBranch key={c.id} node={c} activeSlug={activeSlug} depth={depth + 1} />)}</div>
      )}
    </div>
  );
}

export function RubripediaSidebar() {
  const pathname = usePathname();
  const activeCatSlug = pathname.startsWith("/rubripedia/categoria/") ? pathname.split("/").pop() ?? null : null;
  const activeTagSlug = pathname.startsWith("/rubripedia/etiqueta/") ? pathname.split("/").pop() ?? null : null;
  const onExploreAll = pathname === "/rubripedia";

  const [cats, setCats] = useState<Cat[]>([]);
  const [tags, setTags] = useState<TagItem[]>([]);

  useEffect(() => {
    fetch("/api/rubripedia/categories").then((r) => (r.ok ? r.json() : { categories: [] }))
      .then((d) => setCats(d.categories ?? [])).catch(() => {});
    fetch("/api/rubripedia/tags?limit=40").then((r) => (r.ok ? r.json() : { tags: [] }))
      .then((d) => setTags(d.tags ?? [])).catch(() => {});
  }, []);

  const tree = useMemo(() => buildTree(cats), [cats]);

  return (
    <aside className="sticky top-[49px] hidden h-[calc(100vh-49px)] w-64 shrink-0 overflow-y-auto border-r border-border px-3 py-5 md:block">
      <Link
        href="/rubripedia"
        className={`mb-4 flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-[13px] font-medium transition-colors ${
          onExploreAll ? "bg-primary-bg text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground"
        }`}
      >
        <Sparkles className="h-3.5 w-3.5" /> Explorar todo
      </Link>

      {/* Categorías (árbol completo, navegable) */}
      <div className="mb-5">
        <p className="mb-1.5 flex items-center gap-1.5 px-2.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
          <Layers className="h-3 w-3" /> Categorías
        </p>
        <nav className="space-y-0.5">
          {tree.map((n) => <CatBranch key={n.id} node={n} activeSlug={activeCatSlug} depth={0} />)}
          {tree.length === 0 && <p className="px-2.5 text-[11px] text-muted-foreground/50">Cargando…</p>}
        </nav>
      </div>

      {/* Tags populares */}
      <div>
        <p className="mb-1.5 flex items-center gap-1.5 px-2.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
          <TagIcon className="h-3 w-3" /> Etiquetas
        </p>
        <div className="flex flex-wrap gap-1.5 px-2">
          {tags.map((t) => {
            const active = activeTagSlug === slugify(t.name);
            return (
              <Link
                key={t.name}
                href={tagPath(t.name)}
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors ${
                  active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted-alt hover:text-foreground"
                }`}
              >
                {t.name}
                <span className="tabular-nums opacity-60">{t.count}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </aside>
  );
}
